'use client';
/**
 * components/MonEspaceSheet.tsx — OGRYN
 *
 * Panel slide-in depuis la droite pour "Mon espace".
 * Occupe ~95% de l'écran, laisse ~5% de la carte visible à gauche.
 */

import React, { useState } from 'react';
import Link from 'next/link';
import { useCompte } from '@/lib/auth/client';
import { Connecte, Deconnecte } from '@/components/auth/Connecte';
import BoutonCompte from '@/components/auth/BoutonCompte';
import { trpc } from '@/lib/trpc/client';

type EspaceTab = 'prestataire' | 'client' | 'compte';

// ── Icons ─────────────────────────────────────────────────────────────────────

const I = {
  Hammer:  () => <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 12l-8.5 8.5a2.12 2.12 0 0 1-3-3L12 9"/><path d="M17.64 15L22 10.64"/><path d="M20.91 11.7L16.3 7.09a2.12 2.12 0 0 0-3 3l4.61 4.61a2 2 0 0 0 2.83 0z"/></svg>,
  User:    () => <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
  Settings:() => <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/></svg>,
  BarChart:() => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
  Zap:     () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
  Bell:    () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>,
  Clipboard:()=><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
  Star:    () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
  Wrench:  () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>,
  Trophy:  () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="8 21 12 17 16 21"/><line x1="12" y1="17" x2="12" y2="11"/><path d="M7 4h10l1 6a5 5 0 0 1-5 5h0a5 5 0 0 1-5-5z"/><path d="M7 4a2 2 0 0 0-2 2v2h18V6a2 2 0 0 0-2-2"/></svg>,
  Crown:   () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 20h20M4 20V8l6 5 6-8 6 8v12"/></svg>,
  Qr:      () => <svg width="18" height="18" viewBox="0 0 20 20" fill="none"><rect x="1" y="1" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.5"/><rect x="3" y="3" width="3" height="3" rx="0.5" fill="currentColor"/><rect x="12" y="1" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.5"/><rect x="14" y="3" width="3" height="3" rx="0.5" fill="currentColor"/><rect x="1" y="12" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.5"/><rect x="3" y="14" width="3" height="3" rx="0.5" fill="currentColor"/><rect x="10" y="10" width="2" height="2" fill="currentColor"/><rect x="13" y="10" width="2" height="2" fill="currentColor"/><rect x="16" y="10" width="2" height="2" fill="currentColor"/><rect x="10" y="13" width="2" height="2" fill="currentColor"/><rect x="13" y="13" width="2" height="2" fill="currentColor"/><rect x="10" y="16" width="5" height="2" fill="currentColor"/><rect x="16" y="16" width="2" height="2" fill="currentColor"/></svg>,
  Folder:  () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>,
  PlusCircle:()=><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>,
  MessageCircle:()=><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
  CreditCard:()=><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>,
  Lock:    () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>,
  Sliders: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></svg>,
};

// ── ActionRow ─────────────────────────────────────────────────────────────────

function ActionRow({ icon, title, sub, href, accent }: {
  icon: React.ReactNode; title: string; sub?: string; href: string; accent?: string;
}) {
  return (
    <Link href={href} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 0', borderBottom: '0.5px solid rgba(255,255,255,.06)', textDecoration: 'none' }}>
      <div style={{ width: 32, height: 32, borderRadius: 9, flexShrink: 0, background: 'rgba(255,255,255,.06)', border: '0.5px solid rgba(255,255,255,.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: accent ?? 'rgba(255,255,255,.5)' }}>
        {icon}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: accent ?? 'rgba(255,255,255,.85)' }}>{title}</div>
        {sub && <div style={{ fontSize: 11, color: 'rgba(255,255,255,.38)', marginTop: 1 }}>{sub}</div>}
      </div>
      <span style={{ fontSize: 16, color: 'rgba(255,255,255,.2)' }}>›</span>
    </Link>
  );
}

function StatCard({ value, label, color }: { value: string | number; label: string; color: string }) {
  return (
    <div style={{ textAlign: 'center', background: 'rgba(0,0,0,.2)', borderRadius: 10, padding: '10px 4px' }}>
      <div style={{ fontSize: 22, fontWeight: 800, color }}>{value}</div>
      <div style={{ fontSize: 9, color: 'rgba(255,255,255,.4)', marginTop: 1 }}>{label}</div>
    </div>
  );
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface Props {
  open:    boolean;
  onClose: () => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function MonEspaceSheet({ open, onClose }: Props) {
  const { user } = useCompte();
  const [tab, setTab] = useState<EspaceTab>('prestataire');

  // LES RÔLES SE LISENT EN BASE, PLUS DANS LES MÉTADONNÉES DU COMPTE : chez
  // Clerk, `publicMetadata` était une copie posée par un webhook, qui pouvait
  // être en retard ou n'être jamais écrite en développement.
  const me = trpc.users.me.useQuery(undefined, { enabled: !!user, retry: false, staleTime: 60_000 });
  const isPrestataire = !!me.data?.prestataireProfile;
  const isClient      = !!me.data?.clientProfile;

  const { data: dashData } = trpc.prestataires.getDashboard.useQuery(undefined, {
    enabled: isPrestataire && open,
  });

  const score         = dashData?.scores?.global_score ?? '—';
  const interventions = dashData?.recentInterventions?.length ?? 0;
  const offers        = dashData?.pendingOffers ?? 0;

  const initials = [user?.firstName, user?.lastName]
    .filter(Boolean).map(n => n![0]).join('').toUpperCase() || '?';

  const TABS: { key: EspaceTab; label: string; Icon: () => React.ReactElement }[] = [
    { key: 'prestataire', label: 'Prestataire', Icon: I.Hammer },
    { key: 'client',  label: 'Client',  Icon: I.User   },
    { key: 'compte',  label: 'Compte',  Icon: I.Settings },
  ];

  const tabStyle = (t: EspaceTab): React.CSSProperties => ({
    flex: 1, padding: '10px 4px',
    fontSize: 11, fontWeight: 600, cursor: 'pointer',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
    color: tab === t ? '#2E7D52' : 'rgba(255,255,255,.4)',
    background: 'none', border: 'none',
    borderBottomWidth: 2, borderBottomStyle: 'solid',
    borderBottomColor: tab === t ? '#2E7D52' : 'transparent',
    transition: 'all .15s',
  });

  return (
    <>
      {/* Backdrop flouté (ferme au clic) — ne couvre pas la nav */}
      <div
        style={{
          position: 'fixed',
          top: 0, left: 0, right: 0,
          bottom: 60,
          zIndex: 689,
          background: 'rgba(0,0,0,.25)',
          backdropFilter: 'blur(5px)',
          WebkitBackdropFilter: 'blur(5px)',
          opacity: open ? 1 : 0,
          transition: 'opacity .3s ease',
          pointerEvents: open ? 'auto' : 'none',
        }}
        onClick={onClose}
      />

      {/* Panel slide depuis la droite — bounded (ne touche pas le haut ni le bas) */}
      <div
        style={{
          position:   'fixed',
          top:        'calc(env(safe-area-inset-top) + 52px)',
          bottom:     '60px',
          right:      0,
          width:      'calc(100% - 18px)',
          zIndex:     690,
          transform:  open ? 'translateX(0)' : 'translateX(105%)',
          transition: 'transform .3s cubic-bezier(.16,1,.3,1)', // sans rebond (le 1.2 d'avant dépassait — signalé le 16/08)
          background: '#0f0d0b',
          display:    'flex',
          flexDirection: 'column',
          borderRadius: '18px 0 0 18px',
          border: '0.5px solid rgba(255,255,255,.09)',
          borderRight: 'none',
          overflowY:  'auto',
          scrollbarWidth: 'none',
          boxShadow: '-8px 0 32px rgba(0,0,0,.6)',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 14px',
          borderBottom: '0.5px solid rgba(255,255,255,.07)',
          flexShrink: 0,
          background: 'rgba(8,5,2,.97)',
          position: 'sticky', top: 0, zIndex: 1,
        }}>
          <button
            type="button"
            onClick={onClose}
            style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(255,255,255,.06)', border: '0.5px solid rgba(255,255,255,.10)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, cursor: 'pointer', color: 'rgba(255,255,255,.7)', flexShrink: 0 }}
          >
            ×
          </button>
          <h2 style={{ fontSize: 16, fontWeight: 700, flex: 1, margin: 0, color: 'rgba(255,255,255,.92)' }}>Mon espace</h2>
          <Connecte><BoutonCompte redirectUrl="/" /></Connecte>
          <Deconnecte>
            <Link href="/bienvenue" style={{ fontSize: 12, color: '#2E7D52', textDecoration: 'none', fontWeight: 600 }}>Connexion</Link>
          </Deconnecte>
        </div>

        {/* Profil résumé */}
        <div style={{ padding: '12px 16px 14px', borderBottom: '0.5px solid rgba(255,255,255,.07)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 48, height: 48, borderRadius: 13, flexShrink: 0, background: 'rgba(5,150,105,.2)', border: '2px solid rgba(5,150,105,.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, fontWeight: 800, color: '#2E7D52' }}>
              {initials}
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'rgba(255,255,255,.92)', display: 'flex', alignItems: 'center', gap: 5 }}>
                {user?.name || 'Utilisateur'}
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,.38)', marginTop: 1 }}>
                {user?.email ?? ''}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs prestataire/client/compte */}
        <div style={{ display: 'flex', borderBottom: '0.5px solid rgba(255,255,255,.08)', padding: '0 16px', flexShrink: 0 }}>
          {TABS.map(t => (
            <button key={t.key} type="button" style={tabStyle(t.key)} onClick={() => setTab(t.key)}>
              <t.Icon />
              {t.label}
            </button>
          ))}
        </div>

        {/* Contenu */}
        <div style={{ padding: '10px 16px 20px', flex: 1 }}>

          {/* TAB PRESTATAIRE */}
          {tab === 'prestataire' && (
            isPrestataire ? (
              <>
                <div style={{ background: 'rgba(5,150,105,.07)', border: '0.5px solid rgba(5,150,105,.18)', borderRadius: 14, padding: '12px 14px', marginBottom: 14, marginTop: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                    <span style={{ fontSize: 11, color: 'rgba(255,255,255,.5)' }}>Score global</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                    <StatCard value={score}         label="Score"         color="#2E7D52" />
                    <StatCard value={interventions} label="Interventions" color="#2E7D52" />
                    <StatCard value={offers}        label="Offres"        color="#2563EB" />
                  </div>
                </div>
                <ActionRow icon={<I.BarChart />}    title="Mon espace prestataire" sub="Carte · demandes · tarifs"             href="/prestataire" />
                <ActionRow icon={<I.Wrench />}       title="Mes prestations"       sub="Tarifs · modes d'intervention"          href="/prestataire/profile" />
                <ActionRow icon={<I.Trophy />}       title="Score & statistiques"  sub="Terrain · communauté · fiabilité"       href="/prestataire/stats" />
                <ActionRow icon={<I.Qr />}           title="Mon profil"            sub="Documents · contrat · disponibilité"    href="/prestataire/profile" />
              </>
            ) : (
              <div style={{ padding: '40px 0', textAlign: 'center' }}>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12, color: 'rgba(255,255,255,.2)' }}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 12l-8.5 8.5a2.12 2.12 0 0 1-3-3L12 9"/><path d="M17.64 15L22 10.64"/><path d="M20.91 11.7L16.3 7.09a2.12 2.12 0 0 0-3 3l4.61 4.61a2 2 0 0 0 2.83 0z"/>
                  </svg>
                </div>
                <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>Pas encore de profil prestataire</div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,.4)', marginBottom: 20, lineHeight: 1.6 }}>
                  Créez votre profil pour publier des annonces,<br />recevoir des missions et booster votre visibilité
                </div>
                <Link href="/onboarding" style={{ display: 'inline-block', background: '#2E7D52', color: '#fff', padding: '11px 28px', borderRadius: 12, fontSize: 13, fontWeight: 700, textDecoration: 'none' }}>
                  Créer mon profil prestataire
                </Link>
              </div>
            )
          )}

          {/* TAB CLIENT */}
          {tab === 'client' && (
            isClient ? (
              <>
                <ActionRow icon={<I.Folder />}          title="Mes interventions"  sub="Suivi · historique · avis"      href="/particulier?vue=demandes" />
                <ActionRow icon={<I.PlusCircle />}       title="Nouvelle demande"   sub="Trouver un prestataire vérifié"     href="/particulier" accent="#2563EB" />
                <ActionRow icon={<I.MessageCircle />}    title="Messagerie"         sub="Conversations avec vos prestataires" href="/particulier?vue=messages" />
              </>
            ) : (
              <div style={{ padding: '40px 0', textAlign: 'center' }}>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12, color: 'rgba(255,255,255,.2)' }}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                  </svg>
                </div>
                <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>Pas encore de profil client</div>
                <Link href="/onboarding" style={{ display: 'inline-block', background: '#2563EB', color: '#fff', padding: '11px 28px', borderRadius: 12, fontSize: 13, fontWeight: 700, textDecoration: 'none' }}>
                  Créer mon profil client
                </Link>
              </div>
            )
          )}

          {/* TAB COMPTE */}
          {tab === 'compte' && (
            <>
              <ActionRow icon={<I.Bell />}       title="Notifications"   sub="Push · email · Telegram / WhatsApp" href="/settings/notifications" />
              <ActionRow icon={<I.CreditCard />}  title="Paiements"       sub="Abonnement VIP · historique"        href="/settings/payments" />
              <ActionRow icon={<I.Lock />}        title="Confidentialité" sub="Données · visibilité du profil"     href="/settings/privacy" />
              <ActionRow icon={<I.Sliders />}     title="Paramètres"      sub="Compte · préférences"               href="/settings" />
              <div style={{ marginTop: 20, paddingTop: 12, borderTop: '0.5px solid rgba(255,255,255,.06)' }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'rgba(220,38,38,.7)', cursor: 'pointer' }}>
                  Déconnexion
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
